import { useId, useMemo, useState, type ReactNode } from "react";
import { DenseTable, StatusBadge, useDialogFocus, type Column } from "../../components/index.ts";
import type { RoleState } from "../../state/identity.tsx";
import { WriteButton, ReadOnlyNote, canWrite } from "./WriteControls.tsx";
import {
  useCreateSilence,
  useDeleteSilence,
  useSilences,
  useUpdateSilence,
} from "./use-alerts.ts";
import {
  ALERT_SEVERITY_OPTIONS,
  DEFAULT_MAX_SILENCE_DURATION_S,
  formatDurationSeconds,
  formatTime,
  matcherSummary,
  parseLabelMap,
  severityLabel,
  validateAckReason,
  validateSilenceInput,
  writeErrorMessage,
  type AlertSeverity,
  type Silence,
  type SilenceFormValues,
  type SilenceMatcher,
  type SilenceValidationIssue,
} from "./vocab.ts";

/**
 * The Silences region (task 11.13; design 11 Alerts row, 12.4). It lists silences
 * (`GET /alerts/silences`, viewer), creates (`POST`), changes (`PUT`) and removes (`DELETE`) them
 * (operator). It is the home of the CRITICAL silence invariant:
 *
 *   a silence requires a NON-EMPTY REASON and a BOUNDED DURATION (`ends_at > starts_at`, at most
 *   `max_silence_duration_s`, default 30 days).
 *
 * The create/change form enforces this CLIENT-SIDE: {@link validateSilenceInput} is run on every
 * keystroke, the submit button is DISABLED while it is invalid, and the field-level reasons are
 * shown inline — so a silence with no reason or an unbounded / invalid / over-30-day duration can
 * never be submitted from the UI. The server re-checks the same invariant (it is the authority),
 * and if it STILL rejects (a tighter tenant bound, a matcher the catalog refuses), the form surfaces
 * the server's 400/422 message verbatim. The bound the form uses is the one the server returns on
 * the silence page (`max_silence_duration_s`), not a hard-coded constant.
 *
 * Delete is DESTRUCTIVE, so it is confirmable (design 12.4) and the removal reason is required
 * (recorded in `operator_audit`).
 */
export function Silences(props: { role: RoleState }): ReactNode {
  const query = useSilences();
  const [editing, setEditing] = useState<Silence | "new" | null>(null);
  const [deleting, setDeleting] = useState<Silence | null>(null);

  const maxDurationS = query.data?.max_silence_duration_s ?? DEFAULT_MAX_SILENCE_DURATION_S;

  const columns: readonly Column<Silence>[] = [
    {
      key: "active",
      header: "Active",
      width: "sm",
      cell: (s) =>
        s.active ? (
          <StatusBadge level="warn" label="Active" size="sm" title="Silencing matching alerts now" />
        ) : (
          <StatusBadge level="neutral" label="Inactive" size="sm" />
        ),
    },
    { key: "matcher", header: "Matcher", isRowHeader: true, cell: (s) => matcherSummary(s.matcher) },
    { key: "reason", header: "Reason", cell: (s) => <span className="alr-faint">“{s.reason}”</span> },
    {
      key: "window",
      header: "Window",
      cell: (s) => (
        <span className="alr-num">
          {formatTime(s.starts_at)} → {formatTime(s.ends_at)}
          <span className="alr-faint"> ({formatDurationSeconds((s.ends_at - s.starts_at) / 1000)})</span>
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      cell: (s) => (
        <span className="alr-row-actions">
          <WriteButton role={props.role} onClick={() => setEditing(s)}>
            Change
          </WriteButton>
          <WriteButton role={props.role} onClick={() => setDeleting(s)} variant="danger">
            Remove
          </WriteButton>
        </span>
      ),
    },
  ];

  return (
    <section className="alr-region" aria-label="Silences">
      <div className="alr-region__head">
        <h2 className="alr-region__title">Silences</h2>
        <WriteButton role={props.role} onClick={() => setEditing("new")} variant="primary">
          New silence
        </WriteButton>
      </div>
      <ReadOnlyNote role={props.role} />
      <p className="alr-region__note alr-faint">
        A silence must name a reason and a bounded window, at most {formatDurationSeconds(maxDurationS)}.
      </p>

      {query.isError && !query.error.isWaking ? (
        <p className="alr-field__error" role="alert">
          Silences could not load: {query.error.message}
        </p>
      ) : (
        <DenseTable
          caption="Silences, with their matcher, reason and bounded window."
          columns={columns}
          rows={query.data?.items ?? []}
          rowKey={(s) => s.id}
          emptyMessage="No silences. Create one to suppress matching alerts for a bounded window."
        />
      )}

      {editing !== null && (
        <SilenceForm
          role={props.role}
          existing={editing === "new" ? null : editing}
          maxDurationS={maxDurationS}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting !== null && (
        <DeleteSilenceDialog role={props.role} silence={deleting} onClose={() => setDeleting(null)} />
      )}
    </section>
  );
}

/* ---- Create / change form (the invariant lives here) ---------------------------------------- */

function SilenceForm(props: {
  role: RoleState;
  existing: Silence | null;
  maxDurationS: number;
  onClose: () => void;
}): ReactNode {
  const titleId = useId();
  const create = useCreateSilence();
  const update = useUpdateSilence();
  const mutation = props.existing === null ? create : update;

  const [ruleId, setRuleId] = useState(props.existing?.matcher.rule_id ?? "");
  const [severity, setSeverity] = useState<AlertSeverity | "">(props.existing?.matcher.severity ?? "");
  const [labelsText, setLabelsText] = useState(labelsToText(props.existing?.matcher.labels));
  const [reason, setReason] = useState(props.existing?.reason ?? "");
  const [startsAt, setStartsAt] = useState(toLocalInput(props.existing?.starts_at ?? Date.now()));
  const [endsAt, setEndsAt] = useState(
    toLocalInput(props.existing?.ends_at ?? Date.now() + 60 * 60 * 1000),
  );

  const values = useMemo<SilenceFormValues>(() => {
    const matcher: SilenceMatcher = {};
    if (ruleId.trim().length > 0) matcher.rule_id = ruleId.trim();
    if (severity !== "") matcher.severity = severity;
    const labels = parseLabelMap(labelsText);
    if (Object.keys(labels).length > 0) matcher.labels = labels;
    return {
      matcher,
      reason,
      startsAtMs: fromLocalInput(startsAt),
      endsAtMs: fromLocalInput(endsAt),
    };
  }, [ruleId, severity, labelsText, reason, startsAt, endsAt]);

  const validation = useMemo(
    () => validateSilenceInput(values, props.maxDurationS),
    [values, props.maxDurationS],
  );

  const canSubmit = canWrite(props.role) && validation.ok && !mutation.isPending;

  const submit = (): void => {
    if (!canSubmit || validation.input === undefined) return;
    const onDone = { onSuccess: () => props.onClose() };
    if (props.existing === null) {
      create.mutate(validation.input, onDone);
    } else {
      update.mutate({ id: props.existing.id, body: validation.input }, onDone);
    }
  };

  const issueFor = (field: SilenceValidationIssue["field"]): string | undefined =>
    validation.issues.find((i) => i.field === field)?.message;

  // Trap/restore focus and wire Escape-to-close for the modal (design 12.4).
  const dialogRef = useDialogFocus<HTMLDivElement>(props.onClose);

  return (
    <div className="alr-dialog-backdrop" role="presentation" onClick={props.onClose}>
      <div
        ref={dialogRef}
        className="alr-dialog alr-dialog--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id={titleId} className="alr-dialog__title">
          {props.existing === null ? "New silence" : "Change silence"}
        </h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <fieldset className="alr-fieldset">
            <legend>Matcher (at least one)</legend>
            {issueFor("matcher") !== undefined && (
              <span className="alr-field__error" role="alert">
                {issueFor("matcher")}
              </span>
            )}
            <label className="alr-field">
              <span className="alr-field__label">Rule id</span>
              <input
                className="alr-input"
                value={ruleId}
                onChange={(e) => setRuleId(e.target.value)}
                placeholder="e.g. rule_123"
              />
            </label>
            <label className="alr-field">
              <span className="alr-field__label">Severity</span>
              <select
                className="alr-input"
                value={severity}
                onChange={(e) => setSeverity(e.target.value as AlertSeverity | "")}
              >
                <option value="">(any)</option>
                {ALERT_SEVERITY_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {severityLabel(s)}
                  </option>
                ))}
              </select>
            </label>
            <label className="alr-field">
              <span className="alr-field__label">Labels (one k=v per line)</span>
              <textarea
                className="alr-input"
                value={labelsText}
                onChange={(e) => setLabelsText(e.target.value)}
                rows={2}
                placeholder={"service=gateway-mcp\nnamespace=prod"}
              />
            </label>
          </fieldset>

          <label className="alr-field">
            <span className="alr-field__label">Reason (required)</span>
            <textarea
              className="alr-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={256}
              required
              aria-invalid={issueFor("reason") !== undefined}
            />
            {issueFor("reason") !== undefined && (
              <span className="alr-field__error" role="alert">
                {issueFor("reason")}
              </span>
            )}
          </label>

          <div className="alr-field-row">
            <label className="alr-field">
              <span className="alr-field__label">Starts</span>
              <input
                className="alr-input"
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                aria-invalid={issueFor("starts_at") !== undefined}
              />
              {issueFor("starts_at") !== undefined && (
                <span className="alr-field__error" role="alert">
                  {issueFor("starts_at")}
                </span>
              )}
            </label>
            <label className="alr-field">
              <span className="alr-field__label">Ends</span>
              <input
                className="alr-input"
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                aria-invalid={issueFor("ends_at") !== undefined || issueFor("duration") !== undefined}
              />
              {issueFor("ends_at") !== undefined && (
                <span className="alr-field__error" role="alert">
                  {issueFor("ends_at")}
                </span>
              )}
            </label>
          </div>
          {issueFor("duration") !== undefined && (
            <span className="alr-field__error" role="alert">
              {issueFor("duration")}
            </span>
          )}

          {mutation.isError && (
            <p className="alr-field__error" role="alert">
              {writeErrorMessage(mutation.error, props.existing === null ? "silence" : "silence change")}
            </p>
          )}

          <div className="alr-dialog__actions">
            <WriteButton role={props.role} type="submit" variant="primary" disabled={!canSubmit} onClick={submit}>
              {mutation.isPending ? "Saving…" : props.existing === null ? "Create silence" : "Save changes"}
            </WriteButton>
            <button type="button" className="alr-btn" data-variant="ghost" onClick={props.onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---- Confirmable delete --------------------------------------------------------------------- */

function DeleteSilenceDialog(props: { role: RoleState; silence: Silence; onClose: () => void }): ReactNode {
  const titleId = useId();
  const [reason, setReason] = useState("");
  const mutation = useDeleteSilence();

  const reasonError = validateAckReason(reason);
  const canSubmit = canWrite(props.role) && reasonError === null && !mutation.isPending;

  const submit = (): void => {
    if (!canSubmit) return;
    mutation.mutate(
      { id: props.silence.id, reason: reason.trim() },
      { onSuccess: () => props.onClose() },
    );
  };

  // Trap/restore focus and wire Escape-to-close for the modal (design 12.4).
  const dialogRef = useDialogFocus<HTMLDivElement>(props.onClose);

  return (
    <div className="alr-dialog-backdrop" role="presentation" onClick={props.onClose}>
      <div
        ref={dialogRef}
        className="alr-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id={titleId} className="alr-dialog__title">
          Remove this silence?
        </h3>
        <p className="alr-dialog__body">
          This stops suppressing <strong>{matcherSummary(props.silence.matcher)}</strong>. The removal is
          recorded in the audit log and cannot be undone.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label className="alr-field">
            <span className="alr-field__label">Reason for removal (required)</span>
            <textarea
              className="alr-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={256}
              required
              aria-invalid={reason.length > 0 && reasonError !== null}
            />
            {reason.length > 0 && reasonError !== null && (
              <span className="alr-field__error" role="alert">
                {reasonError}
              </span>
            )}
          </label>
          {mutation.isError && (
            <p className="alr-field__error" role="alert">
              {writeErrorMessage(mutation.error, "silence removal")}
            </p>
          )}
          <div className="alr-dialog__actions">
            <WriteButton role={props.role} type="submit" variant="danger" disabled={!canSubmit} onClick={submit}>
              {mutation.isPending ? "Removing…" : "Remove silence"}
            </WriteButton>
            <button type="button" className="alr-btn" data-variant="ghost" onClick={props.onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---- datetime-local helpers ----------------------------------------------------------------- */

/** Format an epoch-ms into the local value a `datetime-local` input expects (no timezone). */
function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Parse a `datetime-local` value back to epoch-ms, or NaN when empty/invalid (the validator rejects NaN). */
function fromLocalInput(value: string): number {
  if (value.trim().length === 0) return Number.NaN;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : Number.NaN;
}

/** Render a label map back into the textarea's "k=v" per-line form. */
function labelsToText(labels: Readonly<Record<string, string>> | undefined): string {
  if (labels === undefined) return "";
  return Object.entries(labels)
    .map(([k, v]) => `${k}=${v}`)
    .join("\n");
}
